import { Injectable } from '@angular/core';
import { CENTRALIZED_COLORS, ThemeColorConfig } from './constants/theme-colors.constant';

@Injectable({
  providedIn: 'root'
})
export class ThemeColorService {
  private defaultHex = '#94A3B8';

  /**
   * Normalize key to match the centralized mapping.
   */
  private normalizeKey(key: any): string {
    if (key === null || key === undefined) return '';
    return String(key).toLowerCase().trim().replace(/\s+/g, ' ');
  }

  /**
   * Get the color configuration for a given key.
   */
  getConfig(key: string): ThemeColorConfig | undefined {
    return CENTRALIZED_COLORS[this.normalizeKey(key)];
  }

  /**
   * Get the base hex color.
   */
  getHexColor(key: string): string {
    const config = this.getConfig(key);
    return config?.hex || this.defaultHex;
  }

  /**
   * Get text color (hex code). 
   */
  getTextColor(key: string): string {
    return this.getHexColor(key);
  }

  /**
   * Get text color CSS class.
   */
  getTextClass(key: string): string {
    const config = this.getConfig(key);
    return config?.textClass || '';
  }

  /**
   * Get background color (hex with 20% opacity).
   */
  getBackgroundColor(key: string): string {
    return this.getHexColor(key) + '20';
  }

  /**
   * Get background CSS class.
   */
  getBackgroundClass(key: string): string {
    const config = this.getConfig(key);
    return config?.bgClass || '';
  }

  /**
   * Get dynamic inline badge style (background, text color, and border).
   */
  getBadgeStyle(key: string): any {
    const color = this.getHexColor(key);
    return {
      backgroundColor: color + '20',
      color: color,
      border: `1px solid ${color}40`,
    };
  }

  /**
   * Get pre-defined CSS badge class if available.
   */
  getBadgeClass(key: string): string {
    const config = this.getConfig(key);
    return config?.badgeClass || '';
  }

  // --- CARD TYPE NINE SPECIFIC METHODS ---
  // The user requested these to be centralized without altering any output strings.

  getCardNineLabelClass(label: string): string {
    switch ((label || '').toLowerCase()) {
      case 'do now': return 'label-do-now';
      case 'do soon': return 'label-do-soon';
      case 'pending': return 'label-pending';
      case 'do later': return 'label-do-later';
      case 'deployed': return 'label-deployed';
      case 'verified': return 'label-verified';
      case 'audited': return 'label-audited';
      case 'compensated': return 'label-compensated';
      default: return 'label-default';
    }
  }

  getCardNineIconColor(label: string): string {
    switch ((label || '').toLowerCase()) {
      case 'do now':
      case 'overdue_all': return 'label-do-now';
      case 'do soon': return 'label-do-soon';
      case 'pending': return 'label-pending';
      case 'do later': return 'label-do-later';
      case 'deployed':
      case 'implemented': return 'label-deployed';
      case 'verified':
      case 'orphan':
      case 'total': return 'label-verified';
      case 'audited':
      case 'tasks assigned':
      case 'not applicable': return 'label-audited';
      case 'compensated': return 'label-compensated';
      case 'critical': return 'text-critical';
      case 'high': return 'text-high';
      case 'medium': return 'text-medium';
      case 'to do': return 'text-warning';
      case 'low': return 'text-low';
      case 'not implemented': return 'label-do-soon';
      default: return 'label-default';
    }
  }

  getCardNineBorderColor(id: string, isDark: boolean): string {
    const key = (id || '').toLowerCase();

    const light: Record<string, string> = {
      'do now': '#fca5a5', overdue_all: '#fca5a5', critical: '#fca5a5',
      'do soon': '#fdba74', compensated: '#fdba74', high: '#fdba74',
      pending: '#93c5fd', verified: '#93c5fd', orphan: '#93c5fd', total: '#93c5fd',
      implemented: '#86efac', 'not implemented': '#fdba74', 'not applicable': '#d8b4fe',
      'do later': '#86efac', deployed: '#86efac', low: '#86efac',
      audited: '#d8b4fe', 'tasks assigned': '#d8b4fe',
      medium: '#fde047', 'to do': '#fde047',
    };

    const dark: Record<string, string> = {
      'do now': 'rgba(255,77,77,0.35)', overdue_all: 'rgba(255,77,77,0.35)', critical: 'rgba(255,107,107,0.35)',
      'do soon': 'rgba(255,176,64,0.35)', compensated: 'rgba(249,115,22,0.35)', high: 'rgba(255,138,76,0.35)',
      pending: 'rgba(96,165,250,0.35)', verified: 'rgba(59,130,246,0.35)', orphan: 'rgba(59,130,246,0.35)', total: 'rgba(59,130,246,0.35)',
      implemented: 'rgba(34,197,94,0.35)', 'not implemented': 'rgba(255,176,64,0.35)', 'not applicable': 'rgba(168,85,247,0.35)',
      'do later': 'rgba(74,222,128,0.35)', deployed: 'rgba(34,197,94,0.35)', low: 'rgba(74,222,128,0.35)',
      audited: 'rgba(168,85,247,0.35)', 'tasks assigned': 'rgba(168,85,247,0.35)',
      medium: 'rgba(255,224,102,0.35)', 'to do': 'rgba(255,224,102,0.35)',
    };

    return isDark ? (dark[key] ?? 'rgba(255,255,255,0.06)') : (light[key] ?? '#e9ecf1');
  }

  getCardNineProgressBarContainerClass(label: string): string {
    switch ((label || '').toLowerCase()) {
      case 'do now':
      case 'critical':
      case 'overdue_all': return 'bg-red-100';
      case 'do soon':
      case 'high':
      case 'not implemented': return 'bg-orange-100';
      case 'pending':
      case 'verified':
      case 'orphan':
      case 'total': return 'bg-blue-100';
      case 'do later':
      case 'low':
      case 'deployed':
      case 'implemented': return 'bg-green-100';
      case 'audited':
      case 'tasks assigned':
      case 'not applicable': return 'bg-purple-100';
      case 'compensated':
      case 'medium':
      case 'to do': return 'bg-yellow-100';
      default: return 'bg-gray-100';
    }
  }

  getCardNineProgressBarFillClass(label: string): string {
    switch ((label || '').toLowerCase()) {
      case 'do now':
      case 'critical':
      case 'overdue_all': return 'bg-danger';
      case 'do soon':
      case 'high': return 'bg-warning';
      case 'pending': return 'bg-blue-500';
      case 'do later':
      case 'low': return 'bg-green-500';
      case 'deployed': return 'bg-success';
      case 'verified':
      case 'orphan':
      case 'total': return 'bg-verified';
      case 'audited':
      case 'tasks assigned':
      case 'not applicable': return 'bg-audited';
      case 'compensated':
      case 'medium':
      case 'to do': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  }
}
